import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { List, springs } from "@/components/ui";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import type { AuditStatus, UIAudit } from "@/packages/types";
import { AUDIT_STATUS_CONFIG, formatDeadline } from "./constants";

// ─── Stagger constants ───────────────────────────────────────────────────────

/** Max rows that receive incremental delay (avoids long waits on big lists) */
const MAX_STAGGER_INDEX = 12;
/** Seconds between each row entrance */
const STAGGER_STEP = 0.03;

// ─── Props ───────────────────────────────────────────────────────────────────

interface AuditListViewProps {
  groupedAudits: Map<AuditStatus, UIAudit[]>;
  onRowClick: (id: string) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  selectedIds?: Set<string>;
}

function DeadlineText({ deadline }: { deadline: number }) {
  const now = Date.now();
  const daysRemaining = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  const isOverdue = daysRemaining < 0;
  const isUrgent = daysRemaining >= 0 && daysRemaining <= 7;

  return (
    <span
      className={`flex items-center gap-1 text-[11px] ${
        isOverdue ? "text-red-400" : isUrgent ? "text-amber-400" : "text-muted"
      }`}
    >
      <Calendar size={10} className="shrink-0" />
      {formatDeadline(deadline)}
    </span>
  );
}

function ProgressBar({
  total,
  completed,
  dotColor,
  index,
}: {
  total: number;
  completed: number;
  dotColor: string;
  index: number;
}) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2">
      <div className="h-1 w-16 sm:w-20 bg-border/50 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: dotColor }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{
            ...springs.smooth,
            delay: Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP + 0.1,
          }}
        />
      </div>
      <span className="text-[11px] text-muted tabular-nums">
        {completed}/{total}
      </span>
    </div>
  );
}

function AuditRowContent({
  audit,
  config,
  index,
}: {
  audit: UIAudit;
  config: (typeof AUDIT_STATUS_CONFIG)[AuditStatus];
  index: number;
}) {
  return (
    <motion.div
      className="flex flex-col gap-1.5 w-full lg:flex-row lg:items-center lg:gap-4"
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        ...springs.snappy,
        delay: Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP,
      }}
    >
      {/* Row 1: status dot + code + name (full width below lg) */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 lg:flex-1">
        {/* Status dot */}
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: config.dotColor }}
        />

        {/* Code */}
        {audit.code && (
          <span className="text-[11px] text-muted font-mono shrink-0 truncate max-w-36">
            {audit.code}
          </span>
        )}

        {/* Name */}
        <p className="text-sm font-semibold text-primary truncate min-w-0 flex-1">
          {audit.displayName}
        </p>
      </div>

      {/* Row 2 (below lg) / inline right section (lg+) */}
      <div className="flex items-center gap-4 pl-[18px] lg:pl-0 lg:shrink-0">
        {/* Progress bar */}
        <ProgressBar
          total={audit.progress.total}
          completed={audit.progress.completed}
          dotColor={config.dotColor}
          index={index}
        />

        {/* Deadline */}
        <div className="shrink-0 w-28">
          <DeadlineText deadline={audit.deadline} />
        </div>

        {/* Tags */}
        {audit.tags && audit.tags.length > 0 && (
          <div className="flex items-center gap-1.5 lg:min-w-[9rem] lg:justify-end flex-wrap">
            {audit.tags.slice(0, 2).map((tag) => (
              <TagBadge
                key={tag.id}
                name={tag.name}
                color={tag.color ?? "#888"}
              />
            ))}
            {audit.tags.length > 2 && (
              <span
                className="text-[11px] text-muted"
                title={audit.tags
                  .slice(2)
                  .map((t) => t.name)
                  .join(", ")}
              >
                +{audit.tags.length - 2} more
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function AuditListView({
  groupedAudits,
  onRowClick,
  onSelectionChange,
  selectedIds,
}: AuditListViewProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-auto"
    >
      <List.Provider
        onRowClick={onRowClick}
        onSelectionChange={onSelectionChange}
        selectedIds={selectedIds}
      >
        <List>
          {Array.from(groupedAudits.entries()).map(([status, audits]) => {
            if (audits.length === 0) return null;
            const config = AUDIT_STATUS_CONFIG[status];

            return (
              <List.Group
                key={status}
                title={config.label}
                count={audits.length}
                icon={
                  <span
                    className="h-2 w-2 rounded-full inline-block"
                    style={{ backgroundColor: config.dotColor }}
                  />
                }
              >
                {audits.map((audit, i) => (
                  <List.Row key={audit.id} id={audit.id}>
                    <AuditRowContent audit={audit} config={config} index={i} />
                  </List.Row>
                ))}
              </List.Group>
            );
          })}
        </List>
      </List.Provider>
    </motion.div>
  );
}
